import React, { useState, useEffect, useRef } from 'react';
import Loader from '../../components/Loader';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import formatDate from '../../components/formatDate';
import { FaArrowLeft, FaArrowRight, FaExternalLinkAlt, FaUser, FaTimes, FaMoneyBillWave, FaCopy } from 'react-icons/fa';
import { TbReceipt } from 'react-icons/tb';
import { LuDot } from 'react-icons/lu';
import NotFound from '../../components/ul/NotFound';

const PlanHistoryMultiple = () => {
    const [loader, setLoader] = useState(false);
    const [data, setData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const limit = 20;
    const modalRef = useRef(null);

    const fetchData = async () => {
        setLoader(true);
        try {
            const response = await Calling.getMultiplePlanPurchaseUsers(currentPage, limit);
            if (response?.status) {
                setData(response?.data || []);
                setTotalPages(response?.pagination?.totalPages || 1);
                setTotalRecords(response?.pagination?.totalUsers || 0);
            } else {
                toast.error(response?.message);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to fetch data');
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [currentPage]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                setShowModal(false);
                setSelectedUser(null);
            }
        };
        if (showModal) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showModal]);

    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape') {
                setShowModal(false);
                setSelectedUser(null);
            }
        };
        if (showModal) document.addEventListener('keydown', handleEscKey);
        return () => document.removeEventListener('keydown', handleEscKey);
    }, [showModal]);

    const handleUserClick = (userId) => {
        if (userId) {
            window.open(`/manage/user/all/info/${userId}`, '_blank');
        }
    };

    const handleRowClick = (item) => {
        setSelectedUser(item);
        setShowModal(true);
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return <span className='px-3 py-1 w-max text-sm rounded-full bg-green-100 text-green-600 flex items-center'><LuDot size={20} /> Active</span>;
            case 'expired':
                return <span className='px-3 py-1 w-max text-sm rounded-full bg-yellow-100 text-yellow-600 flex items-center'><LuDot size={20} /> Expired</span>;
            case 'cancelled':
                return <span className='px-3 py-1 w-max text-sm rounded-full bg-red-100 text-red-600 flex items-center'><LuDot size={20} /> Cancelled</span>;
            default:
                return <span className='px-3 py-1 w-max text-sm rounded-full bg-gray-100 text-gray-600 flex items-center'><LuDot size={20} /> {status || 'N/A'}</span>;
        }
    };

    const handleCopyTrn = async (trn) => {
        if (!trn) {
            toast.error('No transaction ID to copy');
            return;
        }
        try {
            await navigator.clipboard.writeText(trn);
            toast.success('Transaction ID copied!', { autoClose: 1500 });
        } catch (err) {
            const textArea = document.createElement('textarea');
            textArea.value = trn;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            toast.success('Transaction ID copied!', { autoClose: 1500 });
        }
    };

    // Helper: get latest plan from sorted plans array
    const getLatestPlan = (plans) => {
        if (!plans || plans.length === 0) return null;
        // Sort by createdAt descending and return first
        return [...plans].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
    };

    // Helper: calculate total spent
    const getTotalSpent = (plans) => {
        if (!plans || plans.length === 0) return 0;
        return plans.reduce((sum, plan) => sum + (plan.p_price || 0), 0);
    };

    return (
        <div className="w-full min-h-screen text-main-font">
            {/* Page Header */}
            <div className="mb-6">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-main-blue text-main-white">
                        <TbReceipt size={24} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-main-font">Multiple Plan Purchases</h1>
                        <p className="text-sm text-main-subfont">Users who have purchased multiple plans</p>
                    </div>
                </div>
            </div>

            {/* Main Card */}
            <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden">
                {/* Stats */}
                <div className="p-4">
                    <div className="p-4 rounded-xl border border-main-border bg-main-th">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-main-subfont">Total Users with Multiple Plans</p>
                                <p className="text-2xl font-bold mt-1 text-main-exBlue">
                                    {loader ? (
                                        <span className="flex items-center text-main-subfont text-base">
                                            <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            ...
                                        </span>
                                    ) : (
                                        totalRecords.toLocaleString()
                                    )}
                                </p>
                            </div>
                            <div className="p-3 rounded-xl bg-main-exBlue/10">
                                <TbReceipt size={24} className="text-main-exBlue" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-main-th">
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">#</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">User</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Total Plans</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Total Spent</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Latest Plan</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Status</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Last Purchase</th>
                            </tr>
                        </thead>

                        {loader ? (
                            <tbody>
                                <tr>
                                    <td colSpan={7} className="text-center py-12">
                                        <Loader />
                                    </td>
                                </tr>
                            </tbody>
                        ) : (
                            <tbody className="divide-y divide-main-border">
                                {data.length > 0 ? data.map((item, index) => {
                                    const latestPlan = getLatestPlan(item.plans);
                                    const totalSpent = getTotalSpent(item.plans);

                                    return (
                                        <tr key={item.user?._id || index} className="hover:bg-main-thHover transition-colors cursor-pointer" onClick={() => handleRowClick(item)}>
                                            <td className="px-4 py-4 text-sm text-main-subfont">
                                                {index + 1 + (currentPage - 1) * limit}
                                            </td>
                                            <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                                                <div
                                                    className="flex items-center gap-2 cursor-pointer group"
                                                    onClick={() => handleUserClick(item.user?._id)}
                                                >
                                                    <div className="w-8 h-8 rounded-lg bg-main-blue text-main-white flex items-center justify-center font-bold text-sm">
                                                        {(item.user?.u_name || 'U').charAt(0).toUpperCase()}
                                                    </div>
                                                    <span className="text-main-blue group-hover:underline font-medium flex items-center gap-1">
                                                        {item.user?.u_name || 'N/A'}
                                                        <FaExternalLinkAlt size={10} />
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4">
                                                <span className="px-3 py-1.5 bg-main-blue/10 text-main-blue font-bold rounded-lg text-sm">
                                                    {item.purchaseCount || 0}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4">
                                                <span className="text-main-blue font-bold">₹{totalSpent}</span>
                                            </td>
                                            <td className="px-4 py-4 font-medium text-main-font">
                                                {latestPlan?.p_name || 'N/A'}
                                            </td>
                                            <td className="px-4 py-4">
                                                {getStatusBadge(latestPlan?.status)}
                                            </td>
                                            <td className="px-4 py-4 text-sm text-main-subfont">
                                                {formatDate(latestPlan?.createdAt)}
                                            </td>
                                        </tr>
                                    );
                                }) : (
                                    <tr>
                                        <td colSpan={7} className="py-12">
                                            <NotFound />
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        )}
                    </table>
                </div>

                {/* Pagination */}
                <div className="p-6 border-t border-main-border">
                    <div className="flex flex-col md:flex-row justify-center items-center relative">
                        <div className="flex gap-3 mb-4 md:mb-0">
                            <button
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${currentPage === 1
                                    ? "bg-main-bg border border-main-border text-main-subfont cursor-not-allowed"
                                    : "bg-main-blue text-main-white hover:bg-main-blue/90"
                                    }`}
                            >
                                <FaArrowLeft size={12} /> Previous
                            </button>
                            <button
                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${currentPage === totalPages || totalPages === 0
                                    ? "bg-main-bg border border-main-border text-main-subfont cursor-not-allowed"
                                    : "bg-main-blue text-main-white hover:bg-main-blue/90"
                                    }`}
                            >
                                Next <FaArrowRight size={12} />
                            </button>
                        </div>

                        <p className="text-main-subfont md:absolute md:right-0 text-sm">
                            Page{' '}
                            <span className="px-3 py-1.5 bg-main-th border border-main-border text-main-font font-medium rounded-lg">
                                {currentPage}
                            </span>
                            {' '}of{' '}
                            <span className="font-medium text-main-font">{totalPages}</span>
                        </p>
                    </div>
                </div>
            </div>

            {/* User Plans Detail Modal */}
            {showModal && selectedUser && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div ref={modalRef} className="bg-main-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden border border-main-border">
                        {/* Modal Header */}
                        <div className="bg-main-blue p-5 text-white relative">
                            <button
                                onClick={() => { setShowModal(false); setSelectedUser(null); }}
                                className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-xl transition-colors"
                            >
                                <FaTimes size={16} />
                            </button>
                            <div className="flex items-center gap-3">
                                <div className="p-3 bg-white/20 rounded-xl">
                                    <FaUser size={24} />
                                </div>
                                <div>
                                    <p className="text-white/80 text-sm">Multiple Plan Purchases</p>
                                    <h2 className="text-xl font-bold">{selectedUser?.user?.u_name || 'N/A'}</h2>
                                </div>
                            </div>
                            <div className="mt-3 flex items-center gap-3">
                                <span className="px-3 py-1 text-sm rounded-full bg-white/20 text-white flex items-center gap-1">
                                    <TbReceipt size={14} /> {selectedUser?.purchaseCount || 0} Plans
                                </span>
                                <span className="px-3 py-1 text-sm rounded-full bg-white/20 text-white flex items-center gap-1">
                                    <FaMoneyBillWave size={14} /> ₹{getTotalSpent(selectedUser?.plans)}
                                </span>
                            </div>
                        </div>

                        {/* Modal Body - Plans List */}
                        <div className="p-5 space-y-3 max-h-[50vh] overflow-y-auto">
                            {(selectedUser?.plans || []).length > 0 ? (
                                [...(selectedUser?.plans || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((plan, idx) => (
                                    <div key={plan._id || idx} className="bg-main-th border border-main-border rounded-xl p-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <h3 className="font-semibold text-main-font">{plan?.p_name || 'N/A'}</h3>
                                            {getStatusBadge(plan?.status)}
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 text-sm">
                                            <div>
                                                <p className="text-xs text-main-subfont">Price</p>
                                                <p className="font-bold text-main-blue">₹{plan?.p_price || 0}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-main-subfont">Duration</p>
                                                <p className="font-medium text-main-font">{plan?.p_duration || 0} Days</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-main-subfont">Start Date</p>
                                                <p className="font-medium text-main-font text-xs">{formatDate(plan?.start_date)}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-main-subfont">End Date</p>
                                                <p className="font-medium text-main-font text-xs">{formatDate(plan?.end_date)}</p>
                                            </div>
                                        </div>
                                        {plan?.p_trn && (
                                            <div className="mt-2 flex items-center justify-between bg-main-bg rounded-lg px-3 py-2 border border-main-border">
                                                <span className="font-mono text-xs text-main-subfont">{plan.p_trn}</span>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleCopyTrn(plan.p_trn); }}
                                                    className="p-1.5 hover:bg-main-blue/10 rounded-lg transition-colors"
                                                >
                                                    <FaCopy className="text-main-blue" size={12} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-8 text-main-subfont">
                                    <p>No plan details available</p>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="p-4 bg-main-bg border-t border-main-border">
                            <button
                                onClick={() => handleUserClick(selectedUser?.user?._id)}
                                className="w-full px-4 py-2.5 bg-main-blue text-white rounded-xl hover:bg-main-blue/90 transition-colors flex items-center justify-center gap-2 font-medium"
                            >
                                <FaUser size={14} /> View User Profile
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PlanHistoryMultiple;